import {inject, Injectable} from "@angular/core";
import {
  NgbDateAdapter,
  NgbDateParserFormatter,
  type NgbDateStruct,
  NgbTimeAdapter,
  type NgbTimeStruct,
} from "@ng-bootstrap/ng-bootstrap";
import {DateTime} from "luxon";
import {DateFormats} from "./DateFormats";

@Injectable({providedIn: "root"})
export class LuxonNgbDateAdapter extends NgbDateAdapter<DateTime> {
  public override fromModel(value: DateTime | null): NgbDateStruct | null {
    return value ? {
      day: value.day,
      month: value.month,
      year: value.year,
    } : null;
  }

  // eslint-disable-next-line @typescript-eslint/prefer-readonly-parameter-types
  public override toModel(date: NgbDateStruct | null): DateTime | null {
    return date ? DateTime.fromObject({
      year: date.year,
      month: date.month,
      day: date.day,
    }) : null;
  }
}

@Injectable({providedIn: "root"})
export class LuxonNgbTimeAdapter extends NgbTimeAdapter<DateTime> {
  public override fromModel(value: DateTime | null): NgbTimeStruct | null {
    return value ? {
      hour: value.hour,
      minute: value.minute,
      second: value.second,
    } : null;
  }

  // eslint-disable-next-line @typescript-eslint/prefer-readonly-parameter-types
  public override toModel(date: NgbTimeStruct | null): DateTime | null {
    return date ? DateTime.fromObject({
      hour: date.hour,
      minute: date.minute,
      second: date.second,
    }) : null;
  }
}

@Injectable({providedIn: "root"})
export class LuxonNgbDateParserFormatter extends NgbDateParserFormatter {
  private readonly dateAdapter = inject<NgbDateAdapter<DateTime>>(NgbDateAdapter);

  public override format(date: Readonly<NgbDateStruct> | null): string {
    const dateTime = this.dateAdapter.toModel(date);
    return dateTime?.toLocaleString(DateFormats.dateShortWith2Digits) ?? "";
  }

  // Instance field to avoid conflicts with locale changes.
  private readonly parsingFormat = DateTime.parseFormatForOpts(DateFormats.dateShortWith2Digits) ?? "yyyy-MM-dd";

  public override parse(value: string): NgbDateStruct | null {
    const dateTime = DateTime.fromFormat(value, this.parsingFormat);
    if (!dateTime.isValid) return null;
    return this.dateAdapter.fromModel(dateTime);
  }
}
